import React from 'react';
import { Table } from './pilot-ui/table';
import {
  DeviceDebugResponse,
  DailyCount,
  SensorReading,
  TodayMinMax,
  FirmwareHistoryEntry,
  FirmwareDownload,
  CommEvent,
  CommEventType,
} from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import Button from './ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './pilot-ui/collapsible';

interface DeviceDebugProps {
  data: DeviceDebugResponse;
  onLoadIoTTrail?: (days: number) => void;
  iotTrailLoading?: boolean;
}

const StatusDot = ({ status }: { status: string }) => {
  const color =
    status === 'ok' ? 'bg-status-success-border' :
    status === 'stale' || status === 'minor_drift' || status === 'unknown' ? 'bg-status-warning-border' :
    'bg-status-error-border';
  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
};

const connectionStatus = (minutes: number | null): { label: string; status: string } => {
  if (minutes === null) return { label: 'Unknown', status: 'offline' };
  if (minutes < 120) return { label: `${Math.round(minutes)} min ago`, status: 'ok' };
  if (minutes < 1440) return { label: `${(minutes / 60).toFixed(1)} hrs ago`, status: 'stale' };
  return { label: `${(minutes / 1440).toFixed(1)} days ago`, status: 'offline' };
};

const cToF = (c: number): string => ((c * 9) / 5 + 32).toFixed(1);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-card border border-border rounded-lg shadow-md p-4 mb-4">
    <h3 className="text-lg font-semibold mb-3 border-b pb-2">{title}</h3>
    {children}
  </div>
);

const KV = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between py-1">
    <span className="text-gray-600 text-sm">{label}</span>
    <span className="text-sm font-medium">{value ?? '—'}</span>
  </div>
);

const DeviceIdentitySection = ({ data }: DeviceDebugProps) => {
  const d = data.device;
  return (
    <Section title="Device Identity">
      <KV label="Device Name" value={data.device_name} />
      {d.description && <KV label="Description" value={d.description} />}
      <KV label="Device ID" value={data.id} />
      {d.thing_name && <KV label="Thing Name" value={d.thing_name} />}
      <KV label="Firmware" value={d.firmware_version} />
      <KV label="Bootloader" value={d.bootloader_version} />
      <KV label="WiFi Module" value={d.wifi_version} />
      <KV label="Calibration Offset" value={`${d.calibration_offset} Pa`} />
      <KV label="Fan Side" value={d.x46_fanside ? 'X46' : 'X47'} />
      {d.street_address && (
        <KV label="Location" value={`${d.street_address}, ${d.city}, ${d.state} ${d.postal_code}`} />
      )}
      <KV label="Time Zone" value={d.time_zone} />
      <KV label="Created" value={formatTimestamp(d.created_date)} />
    </Section>
  );
};

const ConnectivityBatterySection = ({ data }: DeviceDebugProps) => {
  const conn = data.connectivity;
  const bat = data.battery;
  const connStatus = connectionStatus(conn.minutes_since_last_reading);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <Section title="Connection Status">
        <div className="flex items-center gap-2 mb-2">
          <StatusDot status={connStatus.status} />
          <span className="font-medium">{connStatus.label}</span>
        </div>
        <KV label="Last Reading" value={formatTimestamp(conn.last_reading_time)} />
        <KV label="First Reading" value={formatTimestamp(conn.first_reading_time)} />
        <KV label="Total Readings" value={conn.total_readings.toLocaleString()} />
      </Section>

      <Section title="Battery">
        <KV label="Latest Voltage" value={bat.latest_voltage !== null ? `${bat.latest_voltage.toFixed(2)} V` : '—'} />
        <KV label="As Of" value={formatTimestamp(bat.latest_timestamp)} />
        <KV label="7 Days Ago" value={bat.voltage_7days_ago !== null ? `${bat.voltage_7days_ago.toFixed(2)} V` : '—'} />
        <KV label="Weekly Delta" value={bat.weekly_delta !== null ? `${bat.weekly_delta > 0 ? '+' : ''}${bat.weekly_delta.toFixed(3)} V` : '—'} />
      </Section>
    </div>
  );
};

const ReadingsBarChart = ({ dailyCounts }: { dailyCounts: DailyCount[] }) => {
  if (!dailyCounts || dailyCounts.length === 0) return <div className="text-gray-600 text-sm">No data</div>;
  const maxCount = Math.max(...dailyCounts.map((d) => d.count), 1);

  return (
    <Section title="Readings Per Day (Last 7 Days)">
      <div className="space-y-1">
        {dailyCounts.map((day) => (
          <div key={day.date} className="flex items-center gap-2 text-sm">
            <span className="w-24 text-gray-600 text-xs">{day.date}</span>
            <div className="flex-1 bg-gray-100 rounded-sm h-5 relative">
              <div
                className="bg-blue-400 h-5 rounded-sm"
                style={{ width: `${(day.count / maxCount) * 100}%` }}
              />
            </div>
            <span className="w-10 text-right text-xs text-gray-600">{day.count}</span>
          </div>
        ))}
      </div>
    </Section>
  );
};

const AnomaliesSection = ({ anomalies }: { anomalies: string[] }) => {
  if (!anomalies || anomalies.length === 0) return null;
  return (
    <div className="bg-status-warning border border-status-warning-border rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold mb-2 text-status-warning-foreground">Anomalies</h3>
      <ul className="list-disc list-inside text-sm text-status-warning-foreground space-y-1">
        {anomalies.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>
    </div>
  );
};

const LatestReadingsSection = ({ readings }: { readings: SensorReading[] }) => {
  if (!readings || readings.length === 0) return null;
  return (
    <Section title="Latest Readings">
      <div className="overflow-x-auto">
        <Table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-600">
              <th className="py-1 pr-3">Time</th>
              <th className="py-1 pr-3">P x46</th>
              <th className="py-1 pr-3">P x47</th>
              <th className="py-1 pr-3">Diff</th>
              <th className="py-1 pr-3">Temp</th>
              <th className="py-1">Humidity</th>
            </tr>
          </thead>
          <tbody>
            {readings.map((r, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-1 pr-3 text-xs">{formatTimestamp(r.time)}</td>
                <td className="py-1 pr-3">{r.pressure_x46.toFixed(1)}</td>
                <td className="py-1 pr-3">{r.pressure_x47.toFixed(1)}</td>
                <td className="py-1 pr-3 font-medium">{r.pressure_diff.toFixed(1)}</td>
                <td className="py-1 pr-3">
                  {r.temperature !== null ? `${r.temperature.toFixed(1)}°C (${cToF(r.temperature)}°F)` : '—'}
                </td>
                <td className="py-1">
                  {r.humidity !== null ? `${r.humidity.toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Section>
  );
};

const SensorRangesSection = ({ minMax }: { minMax: TodayMinMax | null }) => {
  if (!minMax) return null;

  const range = (label: string, min: number | null, max: number | null, unit: string, convertTemp?: boolean) => (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">
        {min !== null && max !== null
          ? convertTemp
            ? `${min.toFixed(1)}–${max.toFixed(1)}${unit} (${cToF(min)}–${cToF(max)}°F)`
            : `${min.toFixed(1)}–${max.toFixed(1)}${unit}`
          : '—'}
      </span>
    </div>
  );

  return (
    <Section title="Today's Sensor Ranges">
      {range('Pressure X46', minMax.pressure_x46_min, minMax.pressure_x46_max, ' Pa')}
      {range('Pressure X47', minMax.pressure_x47_min, minMax.pressure_x47_max, ' Pa')}
      {range('Temperature', minMax.temperature_min, minMax.temperature_max, '°C', true)}
      {range('Humidity', minMax.humidity_min, minMax.humidity_max, '%')}
    </Section>
  );
};

const PipelineSection = ({ data }: DeviceDebugProps) => {
  const p = data.pipeline;
  const recentGaps = p.processing_gaps.filter((g) => {
    const gapDate = new Date(g);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return gapDate >= weekAgo;
  });

  return (
    <Section title="Pipeline Status">
      <KV label="S3 Bundles Today" value={p.s3_bundle_count_today} />
      <KV label="S3 Bundles Yesterday" value={p.s3_bundle_count_yesterday} />
      {p.latest_daily_calc && (
        <>
          <div className="border-t mt-2 pt-2">
            <span className="text-sm font-medium text-gray-700">Latest Daily Calc</span>
          </div>
          <KV label="Date" value={p.latest_daily_calc.date} />
          <KV label="Readings" value={p.latest_daily_calc.readings_count} />
          <KV label="Clusters" value={p.latest_daily_calc.cluster_count} />
          <KV label="Silhouette" value={p.latest_daily_calc.silhouette_score?.toFixed(3)} />
          <KV label="Runtime" value={p.latest_daily_calc.runtime_minutes !== null ? `${p.latest_daily_calc.runtime_minutes} min` : '—'} />
          {p.latest_daily_calc.error_message && (
            <div className="border border-status-error-border bg-status-error text-status-error-foreground text-sm p-2 rounded-sm mt-1">
              {p.latest_daily_calc.error_message}
            </div>
          )}
        </>
      )}
      {p.processing_gaps.length > 0 && (
        <div className="mt-2">
          <span className={`text-sm font-medium ${recentGaps.length > 0 ? 'text-status-error-foreground' : 'text-gray-700'}`}>
            Processing Gaps ({p.processing_gaps.length})
            {recentGaps.length > 0 && ` — ${recentGaps.length} in last 7 days`}
          </span>
          <div className="text-xs text-gray-600 mt-1 max-h-24 overflow-y-auto">
            {p.processing_gaps.join(', ')}
          </div>
        </div>
      )}
    </Section>
  );
};

export interface FirmwareDownloadLines {
  headline: string;
  detail: string | null;
  tone: 'normal' | 'warn' | 'muted';
}

// FS-1014: one sentence for where the Clip's firmware download stands, and a
// second line with when its first and latest blocks arrived.
export const firmwareDownloadLines = (d: FirmwareDownload | null | undefined): FirmwareDownloadLines => {
  if (!d) return { headline: 'No firmware download recorded.', detail: null, tone: 'muted' };

  const v = d.version_label;
  const first = formatTimestamp(d.first_block_at);
  const last = formatTimestamp(d.last_block_at);
  const known = d.total_blocks != null;
  const progress = known
    ? `${d.highest_block} of ${d.total_blocks} blocks${d.percent != null ? ` (${d.percent}%)` : ''}`
    : `block ${d.highest_block} received`;
  const started = `Started ${first}. Latest block ${last}.`;

  switch (d.state) {
    case 'updating':
      return { headline: `Updating to ${v}: ${progress}.`, detail: started, tone: 'normal' };
    case 'downloaded': {
      const received = known ? `all ${d.total_blocks} blocks received` : `block ${d.highest_block} received`;
      return {
        headline: `Downloaded ${v}: ${received}. Waiting for the Clip to restart and report the new version.`,
        detail: `Started ${first}. Finished ${last}.`,
        tone: 'normal',
      };
    }
    case 'done':
      return { headline: `Updated to ${v}.`, detail: `Download started ${first}, finished ${last}.`, tone: 'normal' };
    case 'stalled':
      return {
        headline: `Stalled updating to ${v}: ${progress}. No block for more than ${d.stalled_after_hours} hours.`,
        detail: started,
        tone: 'warn',
      };
    default:
      // A state this page does not know yet (a newer Lambda): show the
      // progress without saying what it means.
      return { headline: `Firmware download to ${v}: ${progress}.`, detail: started, tone: 'normal' };
  }
};

const FIRMWARE_DOWNLOAD_TONE: Record<FirmwareDownloadLines['tone'], string> = {
  normal: '',
  warn: 'text-amber-700 font-medium',
  muted: 'text-gray-600',
};

const FirmwareDownloadStatus = ({ download }: { download: FirmwareDownload | null | undefined }) => {
  const lines = firmwareDownloadLines(download);
  return (
    <div className="mb-3 pb-3 border-b border-border">
      <span className="text-sm font-medium text-gray-700">Firmware download</span>
      <p className={`text-sm mt-1 ${FIRMWARE_DOWNLOAD_TONE[lines.tone]}`}>{lines.headline}</p>
      {lines.detail && <p className="text-xs text-gray-600 mt-0.5">{lines.detail}</p>}
      <p className="text-xs text-gray-600 italic mt-1">From the IoT Core logs, checked every 15 minutes.</p>
    </div>
  );
};

export const FirmwareHistorySection = ({
  entries,
  download,
}: {
  entries: FirmwareHistoryEntry[] | undefined;
  download?: FirmwareDownload | null;
}) => {
  if (!entries || entries.length === 0) {
    return (
      <Section title="Firmware History">
        <FirmwareDownloadStatus download={download} />
        <div className="text-gray-600 text-sm">No firmware changes recorded.</div>
      </Section>
    );
  }
  return (
    <Section title="Firmware History">
      <FirmwareDownloadStatus download={download} />
      <div className="overflow-x-auto">
        <Table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-600">
              <th className="py-1 pr-3">Date</th>
              <th className="py-1 pr-3">Field</th>
              <th className="py-1 pr-3">Old</th>
              <th className="py-1">New</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-1 pr-3 text-xs">{formatTimestamp(e.observed_at)}</td>
                <td className="py-1 pr-3">{e.field_name}</td>
                <td className="py-1 pr-3 text-gray-600">{e.old_value ?? '—'}</td>
                <td className="py-1 font-medium">{e.new_value}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Section>
  );
};

const COMM_TYPE_BADGE: Record<CommEventType, { label: string; cls: string }> = {
  activation: { label: 'activation', cls: 'border border-status-info-border bg-status-info text-status-info-foreground' },
  device_info: { label: 'device info', cls: 'bg-indigo-100 text-indigo-800' },
  field_change: { label: 'field change', cls: 'border border-status-warning-border bg-status-warning text-status-warning-foreground' },
  sensor_bundle: { label: 'sensor bundle', cls: 'border border-status-success-border bg-status-success text-status-success-foreground' },
  firestore: { label: 'firestore', cls: 'bg-purple-100 text-purple-800' },
};

const prettyJSON = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const COMM_PAGE_SIZE = 5;

const CommBadge = ({ type }: { type: CommEventType }) => {
  const badge = COMM_TYPE_BADGE[type] ?? { label: type, cls: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${badge.cls}`}>
      {badge.label}
    </span>
  );
};

const CommHistorySection = ({ history }: { history: DeviceDebugResponse['comm_history'] }) => {
  const events: CommEvent[] = history?.events ?? [];
  const [page, setPage] = React.useState(0);

  // Nest field_change events under the activation/device_info event that
  // triggered them (matched by corr == that event's time). Orphans (no
  // corr, or parent not in the result window) stay as their own row.
  const parentTimes = new Set(
    events.filter((e) => e.type === 'activation' || e.type === 'device_info').map((e) => e.time),
  );
  const childrenByParent = new Map<string, CommEvent[]>();
  const primary: CommEvent[] = [];
  for (const ev of events) {
    if (ev.type === 'field_change' && ev.corr && parentTimes.has(ev.corr)) {
      const list = childrenByParent.get(ev.corr) ?? [];
      list.push(ev);
      childrenByParent.set(ev.corr, list);
    } else {
      primary.push(ev);
    }
  }

  if (primary.length === 0) {
    return (
      <Section title="Communication History">
        <div className="text-gray-600 text-sm">
          No inbound communications recorded — this device has never sent us a message.
        </div>
      </Section>
    );
  }

  const pageCount = Math.ceil(primary.length / COMM_PAGE_SIZE);
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * COMM_PAGE_SIZE;
  const pageEvents = primary.slice(start, start + COMM_PAGE_SIZE);

  return (
    <Section title="Communication History">
      <p className="text-xs text-gray-600 italic mb-2">
        Every inbound message we have a record of, newest first (activation / device-info
        carry the raw payload; the field changes they caused are grouped underneath).
        Independent of whether the device ever produced a sensor reading.
      </p>
      {/* Fixed-height scroll box: keeps the paging controls at a constant
          position regardless of how many rows / expanded payloads the
          current page renders (otherwise the buttons jump every click). */}
      <div className="h-[14.4rem] border border-border rounded-sm [&>[data-slot=table-container]]:h-full [&>[data-slot=table-container]]:overflow-auto">
        <Table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-600 bg-white sticky top-0">
              <th className="py-1 pr-3 pl-1">Time</th>
              <th className="py-1 pr-3">Type</th>
              <th className="py-1">Summary</th>
            </tr>
          </thead>
          <tbody>
            {pageEvents.map((ev, i) => {
              const kids = childrenByParent.get(ev.time) ?? [];
              return (
                <tr key={start + i} className="border-b border-border align-top">
                  <td className="py-1 pr-3 text-xs whitespace-nowrap">{formatTimestamp(ev.time)}</td>
                  <td className="py-1 pr-3"><CommBadge type={ev.type} /></td>
                  <td className="py-1">
                    <span className="font-medium">{ev.summary}</span>
                    {ev.detail && (
                      <Collapsible className="mt-1">
                        <CollapsibleTrigger render={<Button variant="link" size="sm" title="Show or hide this communication event's raw payload" />}>raw payload</CollapsibleTrigger>
                        <CollapsibleContent><pre className="text-xs bg-gray-50 border border-border rounded-sm p-2 mt-1 overflow-x-auto">
                          {prettyJSON(ev.detail)}
                        </pre></CollapsibleContent>
                      </Collapsible>
                    )}
                    {kids.length > 0 && (
                      <ul className="mt-1 ml-3 border-l-2 border-status-warning-border pl-3 space-y-0.5">
                        {kids.map((k, ki) => (
                          <li key={ki} className="text-xs text-gray-600">
                            <span className="text-amber-700">↳ field change</span> {k.summary}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(0)}
              disabled={clampedPage === 0}
              title="Jump to the page with the most recent messages"
            >
              ⏮ Newest
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(clampedPage - 1)}
              disabled={clampedPage === 0}
              title="Show the page of messages just newer than these"
            >
              ← Newer
            </Button>
          </div>
          <span>
            {start + 1}–{Math.min(start + COMM_PAGE_SIZE, primary.length)} of {primary.length}
            {' '}messages · page {clampedPage + 1}/{pageCount}
          </span>
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(clampedPage + 1)}
              disabled={clampedPage >= pageCount - 1}
              title="Show the page of messages just older than these"
            >
              Older →
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(pageCount - 1)}
              disabled={clampedPage >= pageCount - 1}
              title="Jump to the page with the oldest messages in this window"
            >
              Oldest ⏭
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
};

const IOT_DAYS_OPTIONS = [7, 14, 30];

// FS-1001: the query returns only the newest `limit` events, so say so when
// more matched in the window than came back.
export const trailCountLabel = (shown: number, matched: number | null | undefined): string => {
  if (matched != null && matched > shown) {
    return `showing newest ${shown.toLocaleString()} of ${matched.toLocaleString()} events`;
  }
  return `${shown.toLocaleString()} events`;
};

const IoTTrailPanel = ({
  trail,
  onLoad,
  loading,
}: {
  trail: DeviceDebugResponse['iot_trail'];
  onLoad?: (days: number) => void;
  loading?: boolean;
}) => {
  const [days, setDays] = React.useState(7);
  return (
    <Section title="IoT Broker Trail (raw)">
      <p className="text-xs text-gray-600 italic mb-2">
        Connect/disconnect, publishes and rule matches straight from CloudWatch
        (AWSIotLogsV2) — includes traffic that never produced a database row.
        On-demand: the query scans the log group and takes several seconds.
      </p>
      {onLoad && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
          <label htmlFor="iot-trail-window" className="text-gray-600">Window</label>
          <Select value={String(days)} items={IOT_DAYS_OPTIONS.map((day) => ({ value: String(day), label: `${day} days` }))} onValueChange={(value) => { if (value !== null) setDays(Number(value)); }}>
            <SelectTrigger id="iot-trail-window" aria-describedby="iot-trail-window-help" title="Choose how many days of IoT broker logs to query"><SelectValue /></SelectTrigger>
            <SelectContent>
            {IOT_DAYS_OPTIONS.map((d) => (
              <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
            ))}
            </SelectContent>
          </Select>
          <p id="iot-trail-window-help" className="text-xs text-muted-foreground">Controls the log window for the next trail query.</p>
          <Button
            variant="primary"
            onClick={() => onLoad(days)}
            disabled={loading}
            title="Query the IoT broker's CloudWatch logs for this device over the chosen window; takes several seconds"
          >
            {loading ? 'Loading…' : trail ? 'Reload IoT trail' : 'Load IoT trail'}
          </Button>
        </div>
      )}
      {trail && (
        <>
          <div className="text-xs text-gray-600 mb-2">
            Thing: <span className="font-mono">{trail.resolved_thing_name ?? '—'}</span>
            {' · '}source: {trail.source}
            {' · '}window: {trail.window_days}d
            {' · '}
            <span
              className={
                trail.matched != null && trail.matched > trail.events.length
                  ? 'text-amber-700 font-medium'
                  : undefined
              }
            >
              {trailCountLabel(trail.events.length, trail.matched)}
            </span>
          </div>
          {trail.events.length === 0 ? (
            <div className="text-gray-600 text-sm">
              {trail.source === 'unknown'
                ? 'No IoT thing name known — device never sent an identifying message.'
                : 'No broker events in the selected window.'}
            </div>
          ) : (
            <div className="h-[14.4rem] border border-border rounded-sm [&>[data-slot=table-container]]:h-full [&>[data-slot=table-container]]:overflow-auto">
              <Table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600 bg-white sticky top-0">
                    <th className="py-1 pr-3 pl-1">Time</th>
                    <th className="py-1 pr-3">Event</th>
                    <th className="py-1 pr-3">Topic</th>
                    <th className="py-1 pr-3">Rule</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trail.events.map((e, i) => (
                    <tr key={i} className="border-b border-border align-top">
                      <td className="py-1 pr-3 text-xs whitespace-nowrap">{formatTimestamp(e.time)}</td>
                      <td className="py-1 pr-3">{e.event_type}</td>
                      <td className="py-1 pr-3 font-mono text-xs break-all">
                        {e.topic || '—'}
                        {e.payload && (
                          <Collapsible className="mt-1">
                            <CollapsibleTrigger render={<Button variant="link" size="sm" title="Show or hide this IoT event's raw payload" />}>raw payload</CollapsibleTrigger>
                            <CollapsibleContent><pre className="text-xs bg-gray-50 border border-border rounded-sm p-2 mt-1 overflow-x-auto whitespace-pre-wrap">
                              {prettyJSON(e.payload)}
                            </pre></CollapsibleContent>
                          </Collapsible>
                        )}
                      </td>
                      <td className="py-1 pr-3 text-xs">{e.rule || '—'}</td>
                      <td className={`py-1 text-xs ${e.status === 'Failure' ? 'text-status-error-foreground' : 'text-gray-600'}`}>
                        {e.status || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </Section>
  );
};

const formatDrift = (minutes: number): string => {
  const abs = Math.abs(minutes);
  const direction = minutes > 0 ? 'behind' : 'ahead';
  if (abs < 60) return `${abs.toFixed(1)} min ${direction}`;
  if (abs < 1440) return `${(abs / 60).toFixed(1)} hrs ${direction}`;
  return `${(abs / 1440).toFixed(0)} days ${direction}`;
};

const formatSkew = (seconds: number): string => {
  const abs = Math.abs(seconds);
  const direction = seconds > 0 ? 'behind' : 'ahead';
  if (abs < 1) return `${(abs * 1000).toFixed(0)} ms ${direction}`;
  if (abs < 90) return `${abs.toFixed(2)} s ${direction}`;
  if (abs < 3600) return `${(abs / 60).toFixed(2)} min ${direction}`;
  if (abs < 86400) return `${(abs / 3600).toFixed(2)} hrs ${direction}`;
  return `${(abs / 86400).toFixed(1)} days ${direction}`;
};

const connectionDescription = (status: string, minutes: number | null): string => {
  if (minutes === null) return 'No readings found in the database for this device.';
  const ago = minutes < 60 ? `${Math.round(minutes)} min ago` :
    minutes < 1440 ? `${(minutes / 60).toFixed(1)} hrs ago` :
    `${(minutes / 1440).toFixed(1)} days ago`;
  switch (status) {
    case 'ok': return `Last DB reading ${ago}. Device is actively sending data.`;
    case 'stale': return `Last DB reading ${ago}. Device has missed at least one expected connection window (~every 4 hrs).`;
    case 'offline': return `Last DB reading ${ago}. Device has not sent data in over 24 hours — may be powered off, out of WiFi range, or its clock is wrong (check clock status).`;
    default: return `Last DB reading ${ago}.`;
  }
};

const clockDescription = (status: string, driftMinutes: number | null): string => {
  if (driftMinutes === null) return 'No S3 bundles found — cannot determine clock accuracy.';
  switch (status) {
    case 'ok': return 'Device clock is within expected range. Drift under 12 hours is normal due to batched uploads.';
    case 'minor_drift': return 'Device clock is drifting. Not yet causing data loss but should be investigated.';
    case 'clock_error': return 'Device clock is severely wrong. Readings land with incorrect timestamps and may be archived immediately, making them invisible in the database.';
    default: return '';
  }
};

const ClockHealthSection = ({ clock }: { clock: DeviceDebugResponse['clock_health'] }) => {
  if (!clock) return null;

  const isClockError = clock.clock_status === 'clock_error';
  const driftDisplay = clock.clock_drift_minutes !== null ? formatDrift(clock.clock_drift_minutes) : null;

  return (
    <Section title="Clock Health">
      <div className="space-y-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <StatusDot status={clock.connection_status} />
            <span className="text-sm font-medium capitalize">Connection: {clock.connection_status}</span>
          </div>
          <p className="text-xs text-gray-600 ml-5 mt-0.5">{connectionDescription(clock.connection_status, clock.minutes_since_last_reading)}</p>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <StatusDot status={clock.clock_status} />
            <span className="text-sm font-medium capitalize">Clock: {clock.clock_status.replace('_', ' ')}</span>
          </div>
          <p className="text-xs text-gray-600 ml-5 mt-0.5">{clockDescription(clock.clock_status, clock.clock_drift_minutes)}</p>
        </div>
      </div>
      {(driftDisplay || clock.device_clock_time || clock.bundle_received_at) && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-gray-600 mb-1">S3 bundle clock check (coarse — most-recent bundle only)</p>
          <p className="text-xs text-gray-600 italic mb-2">
            Compares the device's TIME0 (first-sample timestamp inside the bundle) against the S3 object's LastModified.
            Healthy devices report ≈4 hours "behind" — that's the chunk duration, not real drift. Investigate when ≥ 12 h.
          </p>
          {driftDisplay && (
            <KV
              label="Clock Drift"
              value={<span className={isClockError ? 'text-status-error-foreground font-semibold' : ''}>{driftDisplay}</span>}
            />
          )}
          <p className="text-xs text-gray-600 italic mb-2 ml-1">= Bundle Received − Device Clock. Positive = device behind server.</p>
          {clock.device_clock_time && <KV label="Device Clock" value={formatTimestamp(clock.device_clock_time)} />}
          <p className="text-xs text-gray-600 italic mb-2 ml-1">Wall clock the device assigned to the FIRST sample in the most recent S3 bundle (TIME0 + epoch-2000).</p>
          {clock.bundle_received_at && <KV label="Bundle Received" value={formatTimestamp(clock.bundle_received_at)} />}
          <p className="text-xs text-gray-600 italic mb-2 ml-1">S3 LastModified — when the IoT rule wrote that bundle to S3 (server clock).</p>
        </div>
      )}
      <KV label="Last DB Reading" value={formatTimestamp(clock.last_reading_time)} />
      <p className="text-xs text-gray-600 italic mb-2 ml-1">Newest row in <code className="text-gray-600">readings</code> for this device. Now uses TIMENOW-corrected timestamps on FW v0.07+.</p>
      {(clock.latest_skew_seconds !== null || clock.skew_24h_avg_seconds !== null) && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-gray-600 mb-1">Per-packet TIMENOW skew (FW v0.07+)</p>
          <p className="text-xs text-gray-600 italic mb-2">
            Skew = IoT-rule arrivalTime − device TIMENOW (per packet, ~6/day). Sub-second precision; positive = device clock behind server.
            Applied as an offset to TIME0 so reading timestamps land on real wall clock even if the device clock has drifted.
          </p>
          {clock.latest_skew_seconds !== null && (
            <KV
              label="Latest Skew"
              value={
                <span>
                  {formatSkew(clock.latest_skew_seconds)}
                  {clock.latest_skew_timestamp && (
                    <span className="text-gray-600 text-xs ml-2">at {formatTimestamp(clock.latest_skew_timestamp)}</span>
                  )}
                </span>
              }
            />
          )}
          {clock.skew_24h_avg_seconds !== null && (
            <KV
              label="24h Avg Skew"
              value={
                <span>
                  {formatSkew(clock.skew_24h_avg_seconds)}
                  <span className="text-gray-600 text-xs ml-2">({clock.skew_24h_sample_count} packets)</span>
                </span>
              }
            />
          )}
        </div>
      )}
    </Section>
  );
};

const DeviceDebug: React.FC<DeviceDebugProps> = ({ data, onLoadIoTTrail, iotTrailLoading }) => {
  return (
    <div className="max-w-7xl mx-auto p-2">
      <div className="text-xs text-gray-600 mb-2 text-right">
        Generated: {formatTimestamp(data.generated_at)}
      </div>

      {data.errors && data.errors.length > 0 && (
        <div className="bg-red-50 border border-status-error-border rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-2 text-red-800">Errors</h3>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {data.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <AnomaliesSection anomalies={data.anomalies} />
      <DeviceIdentitySection data={data} />
      <ConnectivityBatterySection data={data} />
      <CommHistorySection history={data.comm_history} />
      <IoTTrailPanel trail={data.iot_trail} onLoad={onLoadIoTTrail} loading={iotTrailLoading} />
      <ReadingsBarChart dailyCounts={data.connectivity.daily_counts} />
      <LatestReadingsSection readings={data.sensor_snapshot.last_readings} />
      <SensorRangesSection minMax={data.sensor_snapshot.today_min_max} />
      <PipelineSection data={data} />
      <FirmwareHistorySection entries={data.firmware_history?.entries} download={data.firmware_download} />
      <ClockHealthSection clock={data.clock_health} />
    </div>
  );
};

export default DeviceDebug;
